import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { externalApi, type FileUpload } from '@/api/external';

// ============== Query Hooks ==============

/**
 * Validates an external link and returns account + credit analysis data
 * Don't retry on invalid/expired links since those are expected states
 */
export function useValidateExternalLink(linkId: string, email: string) {
  return useQuery({
    queryKey: ['externalLink', linkId, email],
    queryFn: () => externalApi.validateLink(linkId, email),
    enabled: !!linkId && !!email,
    retry: false, // Don't retry on invalid/expired links
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Gets the current document state for an external link
 * Refetches on window focus to keep document status up-to-date
 */
export function useExternalDocumentState(linkId: string, email: string) {
  return useQuery({
    queryKey: ['externalDocuments', linkId, email],
    queryFn: () => externalApi.getDocumentState(linkId, email),
    enabled: !!linkId && !!email,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000, // 30 seconds
  });
}

// ============== Mutation Hooks ==============

interface UploadExternalDocumentsParams {
  linkId: string;
  email: string;
  files: FileUpload[];
}

/**
 * Uploads documents for an external link
 * Automatically invalidates the document state query on success
 */
export function useUploadExternalDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ linkId, email, files }: UploadExternalDocumentsParams) =>
      externalApi.uploadDocuments(linkId, email, files),
    onSuccess: (_, { linkId, email }) => {
      // Invalidate document state to reflect the newly uploaded documents
      queryClient.invalidateQueries({
        queryKey: ['externalDocuments', linkId, email],
      });
    },
  });
}
